import React, { useState, useEffect, useCallback } from 'react';
import { getTabStyles, getTabHoverStyles, restoreTabStyles } from '../../utils/tabColors';
import scriptPermissionsService from '../../services/scriptPermissionsService';
import Toast from '../Toast';

const ScriptGovernanceTab = ({ scopeType, scopeId }) => {
  const [subTab, setSubTab] = useState('owner-permissions');
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmToast, setConfirmToast] = useState({ show: false, message: '', onConfirm: null, onCancel: null });

  const subTabs = [
    { id: 'owner-permissions', label: 'Owner Permissions', count: null },
    { id: 'viewer-permissions', label: 'Viewer Permissions', count: null },
    { id: 'editor-permissions', label: 'Editor Permissions', count: null },
    { id: 'executor-permissions', label: 'Executor Permissions', count: null },
    { id: 'share-management', label: 'Share Management', count: null }
  ];

  return (
    <div className="dashboard-card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 20, fontWeight: 600 }}>
        Script Permissions
      </h2>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e5e7eb', overflowX: 'auto' }}>
        {subTabs.map(t => (
          <button
            key={t.id}
            className="tab-button"
            style={getTabStyles(2, subTab === t.id)}
            onMouseEnter={(e) => {
              if (subTab !== t.id) {
                Object.assign(e.currentTarget.style, getTabHoverStyles(2));
              }
            }}
            onMouseLeave={(e) => {
              if (subTab !== t.id) {
                restoreTabStyles(e.currentTarget, 2, false);
              }
            }}
            onClick={() => setSubTab(t.id)}
          >
            {t.label}
            {t.count !== null && t.count > 0 && (
              <span style={{ marginLeft: 8, padding: '2px 6px', background: '#4f46e5', color: '#fff', borderRadius: 12, fontSize: 11 }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>
          Error: {error}
        </div>
      )}

      {subTab === 'owner-permissions' && (
        <OwnerPermissionsSection scopeType={scopeType} scopeId={scopeId} onError={setError} onToast={setToast} onConfirm={setConfirmToast} />
      )}
      {subTab === 'viewer-permissions' && (
        <ViewerPermissionsSection scopeType={scopeType} scopeId={scopeId} onError={setError} onToast={setToast} onConfirm={setConfirmToast} />
      )}
      {subTab === 'editor-permissions' && (
        <EditorPermissionsSection scopeType={scopeType} scopeId={scopeId} onError={setError} onToast={setToast} onConfirm={setConfirmToast} />
      )}
      {subTab === 'executor-permissions' && (
        <ExecutorPermissionsSection scopeType={scopeType} scopeId={scopeId} onError={setError} onToast={setToast} onConfirm={setConfirmToast} />
      )}
      {subTab === 'share-management' && (
        <ShareManagementSection scopeType={scopeType} scopeId={scopeId} onError={setError} onToast={setToast} onConfirm={setConfirmToast} />
      )}

      {/* Toast Components */}
      <Toast 
        message={toast.message} 
        type={toast.type} 
        show={toast.show} 
        onClose={() => setToast({ ...toast, show: false })} 
        duration={toast.duration || 5000}
      />
      <Toast 
        message={confirmToast.message} 
        type="warning"
        show={confirmToast.show} 
        persist={true}
        actionLabel="Confirm"
        onAction={confirmToast.onConfirm}
        secondaryActionLabel="Cancel"
        onSecondaryAction={confirmToast.onCancel}
        onClose={() => setConfirmToast({ show: false, message: '', onConfirm: null, onCancel: null })} 
      />
    </div>
  );
};

// ==================== OWNER PERMISSIONS SECTION ====================
const OwnerPermissionsSection = ({ scopeType, scopeId, onError, onToast, onConfirm }) => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', sortBy: 'modified', sortDir: 'desc' });
  const [selectedScripts, setSelectedScripts] = useState(new Set());
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTargetScript, setTransferTargetScript] = useState(null);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);

  const loadScripts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scriptPermissionsService.getScripts(scopeType, scopeId || null, {
        ...filters,
        page,
        pageSize
      });
      setScripts(data.scripts || []);
      setTotal(data.total || 0);
    } catch (err) {
      onError(err.message);
      console.error('Error loading scripts:', err);
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, filters, page, pageSize, onError]);

  useEffect(() => {
    if (scopeType && (scopeId || scopeType === 'Global')) {
      loadScripts();
    }
  }, [scopeType, scopeId, filters, page, pageSize, loadScripts]);

  const handleTransferOwnership = (script) => {
    setTransferTargetScript(script);
    setNewOwnerId('');
    setShowTransferModal(true);
  };

  const confirmTransferOwnership = async () => {
    if (!transferTargetScript || !newOwnerId.trim()) {
      onToast({ show: true, message: 'Please enter a new owner ID', type: 'error' });
      return;
    }

    try {
      await scriptPermissionsService.transferOwnership(
        transferTargetScript.id,
        newOwnerId.trim(),
        scopeType,
        scopeId || null
      );
      onToast({ show: true, message: `Ownership transferred successfully to ${newOwnerId}`, type: 'success' });
      setShowTransferModal(false);
      setTransferTargetScript(null);
      setNewOwnerId('');
      loadScripts();
    } catch (err) {
      onError(err.message);
      onToast({ show: true, message: `Failed to transfer ownership: ${err.message}`, type: 'error' });
    }
  };

  const toggleScriptSelection = (scriptId) => {
    const newSelected = new Set(selectedScripts);
    if (newSelected.has(scriptId)) {
      newSelected.delete(scriptId);
    } else {
      newSelected.add(scriptId);
    }
    setSelectedScripts(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedScripts.size === scripts.length) {
      setSelectedScripts(new Set());
    } else {
      setSelectedScripts(new Set(scripts.map(s => s.id)));
    }
  };

  return (
    <div>
      {/* Owner Rights Documentation */}
      <div style={{ marginBottom: 20, padding: 16, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#0369a1' }}>Owner Permissions</h4>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 14, color: '#075985' }}>
          <li>Full control over script (view, edit, delete, execute)</li>
          <li>Can transfer ownership to another user</li>
          <li>Can share script with others (Viewer, Editor, Executor roles)</li>
          <li>Can modify script metadata and settings</li>
          <li>Cannot be revoked by other users</li>
        </ul>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search scripts..."
          value={filters.search}
          onChange={(e) => {
            setFilters({ ...filters, search: e.target.value });
            setPage(1);
          }}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, flex: 1, minWidth: 200 }}
        />
        <select
          value={filters.sortBy}
          onChange={(e) => {
            setFilters({ ...filters, sortBy: e.target.value });
            setPage(1);
          }}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        >
          <option value="modified">Last Modified</option>
          <option value="created">Created Date</option>
          <option value="name">Name</option>
        </select>
        <button
          onClick={() => {
            setFilters({ search: '', sortBy: 'modified', sortDir: 'desc' });
            setPage(1);
          }}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        >
          Clear
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedScripts.size > 0 && (
        <div style={{ marginBottom: 16, padding: 12, background: '#eff6ff', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{selectedScripts.size} script(s) selected</span>
          <button
            onClick={() => setSelectedScripts(new Set())}
            style={{ padding: '6px 12px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Scripts Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading scripts...</div>
      ) : scripts.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No scripts found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectedScripts.size === scripts.length && scripts.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Script Name</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Current Owner</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Created</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Last Modified</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map(script => (
                <tr key={script.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedScripts.has(script.id)}
                      onChange={() => toggleScriptSelection(script.id)}
                    />
                  </td>
                  <td style={{ padding: '12px', fontWeight: 500 }}>{script.name}</td>
                  <td style={{ padding: '12px', color: '#6b7280' }}>{script.ownerEmail || script.userId}</td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: 12 }}>
                    {new Date(script.created || script.createdDate).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: 12 }}>
                    {new Date(script.modified || script.modifiedDate).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleTransferOwnership(script)}
                      style={{ padding: '4px 8px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      Transfer Ownership
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#6b7280', fontSize: 14 }}>
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} scripts
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: page === 1 ? '#f3f4f6' : '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page * pageSize >= total}
                style={{ padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: page * pageSize >= total ? '#f3f4f6' : '#fff', cursor: page * pageSize >= total ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && transferTargetScript && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 500, width: '90%' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>Transfer Ownership</h3>
            <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: 14 }}>
              Transfer ownership of <strong>{transferTargetScript.name}</strong> to a new owner.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>New Owner ID</label>
              <input
                type="text"
                value={newOwnerId}
                onChange={(e) => setNewOwnerId(e.target.value)}
                placeholder="Enter user ID or email"
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferTargetScript(null);
                  setNewOwnerId('');
                }}
                style={{ padding: '10px 20px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onConfirm({
                    show: true,
                    message: `Are you sure you want to transfer ownership of "${transferTargetScript.name}" to ${newOwnerId || 'the new owner'}? This action cannot be undone.`,
                    onConfirm: () => {
                      onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
                      confirmTransferOwnership();
                    },
                    onCancel: () => {
                      onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
                    }
                  });
                }}
                disabled={!newOwnerId.trim()}
                style={{ padding: '10px 20px', background: !newOwnerId.trim() ? '#9ca3af' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: !newOwnerId.trim() ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}
              >
                Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== VIEWER PERMISSIONS SECTION ====================
const ViewerPermissionsSection = ({ scopeType, scopeId, onError, onToast, onConfirm }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', scriptId: '' });
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scriptPermissionsService.getPermissionsByRole(scopeType, scopeId || null, 'viewer');
      setPermissions(data.permissions || data || []);
    } catch (err) {
      onError(err.message);
      console.error('Error loading viewer permissions:', err);
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, onError]);

  useEffect(() => {
    if (scopeType && (scopeId || scopeType === 'Global')) {
      loadPermissions();
    }
  }, [scopeType, scopeId, loadPermissions]);

  const handleRevoke = async (permissionId, scriptId, scriptName, principalName) => {
    onConfirm({
      show: true,
      message: `Are you sure you want to revoke viewer access for ${principalName} on script "${scriptName}"?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          await scriptPermissionsService.removeScriptShare(scriptId, permissionId, scopeType, scopeId || null);
          await loadPermissions();
          onToast({ show: true, message: 'Viewer access revoked successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to revoke access: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleBulkRevoke = () => {
    if (selectedPermissions.size === 0) return;
    onConfirm({
      show: true,
      message: `Are you sure you want to revoke viewer access for ${selectedPermissions.size} permission(s)?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          // Get script IDs from selected permissions
          const permissionsToRevoke = permissions.filter(p => selectedPermissions.has(p.id));
          const scriptIds = [...new Set(permissionsToRevoke.map(p => p.scriptId))];
          
          await scriptPermissionsService.bulkRevokePermissions(scriptIds, 'viewer', scopeType, scopeId || null);
          await loadPermissions();
          setSelectedPermissions(new Set());
          onToast({ show: true, message: 'Viewer access revoked successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to revoke access: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const filteredPermissions = permissions.filter(p => {
    if (filters.search && !p.scriptName?.toLowerCase().includes(filters.search.toLowerCase()) && 
        !p.principalName?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.scriptId && p.scriptId?.toString() !== filters.scriptId) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Viewer Capabilities Reference */}
      <div style={{ marginBottom: 20, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#166534' }}>Viewer Capabilities</h4>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 14, color: '#15803d' }}>
          <li>Can view script content (read-only)</li>
          <li>Cannot execute the script</li>
          <li>Cannot edit or modify the script</li>
          <li>Cannot share the script with others</li>
        </ul>
      </div>

      {/* Filters and Actions */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by script or principal..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, flex: 1, minWidth: 200 }}
        />
        <input
          type="text"
          placeholder="Script ID"
          value={filters.scriptId}
          onChange={(e) => setFilters({ ...filters, scriptId: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, width: 120 }}
        />
        <button
          onClick={() => setShowAddDialog(true)}
          style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
        >
          Add Viewer
        </button>
        {selectedPermissions.size > 0 && (
          <button
            onClick={handleBulkRevoke}
            style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
          >
            Revoke Selected ({selectedPermissions.size})
          </button>
        )}
      </div>

      {/* Permissions Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading viewer permissions...</div>
      ) : filteredPermissions.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No viewer permissions found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectedPermissions.size === filteredPermissions.length && filteredPermissions.length > 0}
                    onChange={() => {
                      if (selectedPermissions.size === filteredPermissions.length) {
                        setSelectedPermissions(new Set());
                      } else {
                        setSelectedPermissions(new Set(filteredPermissions.map(p => p.id)));
                      }
                    }}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Script</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Principal</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Granted</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Expires</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.map(perm => (
                <tr key={perm.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.has(perm.id)}
                      onChange={() => {
                        const newSelected = new Set(selectedPermissions);
                        if (newSelected.has(perm.id)) {
                          newSelected.delete(perm.id);
                        } else {
                          newSelected.add(perm.id);
                        }
                        setSelectedPermissions(newSelected);
                      }}
                    />
                  </td>
                  <td style={{ padding: '12px' }}>{perm.scriptName || `Script #${perm.scriptId}`}</td>
                  <td style={{ padding: '12px' }}>{perm.principalName || perm.principalId}</td>
                  <td style={{ padding: '12px', textTransform: 'capitalize' }}>{perm.principalType || 'user'}</td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: 12 }}>
                    {perm.grantedAt ? new Date(perm.grantedAt).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px', color: perm.expiresAt && new Date(perm.expiresAt) < new Date() ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                    {perm.expiresAt ? new Date(perm.expiresAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleRevoke(perm.id, perm.scriptId, perm.scriptName || `Script #${perm.scriptId}`, perm.principalName || perm.principalId)}
                      style={{ padding: '4px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Viewer Dialog */}
      {showAddDialog && (
        <AddPermissionDialog
          role="viewer"
          scopeType={scopeType}
          scopeId={scopeId}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            loadPermissions();
            onToast({ show: true, message: 'Viewer access granted successfully', type: 'success' });
          }}
        />
      )}
    </div>
  );
};

// ==================== EDITOR PERMISSIONS SECTION ====================
const EditorPermissionsSection = ({ scopeType, scopeId, onError, onToast, onConfirm }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', scriptId: '' });
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scriptPermissionsService.getPermissionsByRole(scopeType, scopeId || null, 'editor');
      setPermissions(data.permissions || data || []);
    } catch (err) {
      onError(err.message);
      console.error('Error loading editor permissions:', err);
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, onError]);

  useEffect(() => {
    if (scopeType && (scopeId || scopeType === 'Global')) {
      loadPermissions();
    }
  }, [scopeType, scopeId, loadPermissions]);

  const handleRevoke = async (permissionId, scriptId, scriptName, principalName) => {
    onConfirm({
      show: true,
      message: `Are you sure you want to revoke editor access for ${principalName} on script "${scriptName}"?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          await scriptPermissionsService.removeScriptShare(scriptId, permissionId, scopeType, scopeId || null);
          await loadPermissions();
          onToast({ show: true, message: 'Editor access revoked successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to revoke access: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleBulkRevoke = () => {
    if (selectedPermissions.size === 0) return;
    onConfirm({
      show: true,
      message: `Are you sure you want to revoke editor access for ${selectedPermissions.size} permission(s)?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          // Get script IDs from selected permissions
          const permissionsToRevoke = permissions.filter(p => selectedPermissions.has(p.id));
          const scriptIds = [...new Set(permissionsToRevoke.map(p => p.scriptId))];
          
          await scriptPermissionsService.bulkRevokePermissions(scriptIds, 'editor', scopeType, scopeId || null);
          await loadPermissions();
          setSelectedPermissions(new Set());
          onToast({ show: true, message: 'Editor access revoked successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to revoke access: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const filteredPermissions = permissions.filter(p => {
    if (filters.search && !p.scriptName?.toLowerCase().includes(filters.search.toLowerCase()) && 
        !p.principalName?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.scriptId && p.scriptId?.toString() !== filters.scriptId) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Editor Capabilities Reference */}
      <div style={{ marginBottom: 20, padding: 16, background: '#fef3c7', borderRadius: 8, border: '1px solid #fde68a' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#92400e' }}>Editor Capabilities</h4>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 14, color: '#78350f' }}>
          <li>Can view and modify script content</li>
          <li>Can update script metadata and settings</li>
          <li>Cannot execute the script</li>
          <li>Cannot transfer ownership</li>
          <li>Cannot delete the script</li>
        </ul>
      </div>

      {/* Filters and Actions */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by script or principal..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, flex: 1, minWidth: 200 }}
        />
        <input
          type="text"
          placeholder="Script ID"
          value={filters.scriptId}
          onChange={(e) => setFilters({ ...filters, scriptId: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, width: 120 }}
        />
        <button
          onClick={() => setShowAddDialog(true)}
          style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
        >
          Add Editor
        </button>
        {selectedPermissions.size > 0 && (
          <button
            onClick={handleBulkRevoke}
            style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
          >
            Revoke Selected ({selectedPermissions.size})
          </button>
        )}
      </div>

      {/* Permissions Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading editor permissions...</div>
      ) : filteredPermissions.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No editor permissions found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectedPermissions.size === filteredPermissions.length && filteredPermissions.length > 0}
                    onChange={() => {
                      if (selectedPermissions.size === filteredPermissions.length) {
                        setSelectedPermissions(new Set());
                      } else {
                        setSelectedPermissions(new Set(filteredPermissions.map(p => p.id)));
                      }
                    }}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Script</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Principal</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Granted</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Expires</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.map(perm => (
                <tr key={perm.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.has(perm.id)}
                      onChange={() => {
                        const newSelected = new Set(selectedPermissions);
                        if (newSelected.has(perm.id)) {
                          newSelected.delete(perm.id);
                        } else {
                          newSelected.add(perm.id);
                        }
                        setSelectedPermissions(newSelected);
                      }}
                    />
                  </td>
                  <td style={{ padding: '12px' }}>{perm.scriptName || `Script #${perm.scriptId}`}</td>
                  <td style={{ padding: '12px' }}>{perm.principalName || perm.principalId}</td>
                  <td style={{ padding: '12px', textTransform: 'capitalize' }}>{perm.principalType || 'user'}</td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: 12 }}>
                    {perm.grantedAt ? new Date(perm.grantedAt).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px', color: perm.expiresAt && new Date(perm.expiresAt) < new Date() ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                    {perm.expiresAt ? new Date(perm.expiresAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleRevoke(perm.id, perm.scriptId, perm.scriptName || `Script #${perm.scriptId}`, perm.principalName || perm.principalId)}
                      style={{ padding: '4px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Editor Dialog */}
      {showAddDialog && (
        <AddPermissionDialog
          role="editor"
          scopeType={scopeType}
          scopeId={scopeId}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            loadPermissions();
            onToast({ show: true, message: 'Editor access granted successfully', type: 'success' });
          }}
        />
      )}
    </div>
  );
};

// ==================== EXECUTOR PERMISSIONS SECTION ====================
const ExecutorPermissionsSection = ({ scopeType, scopeId, onError, onToast, onConfirm }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', scriptId: '' });
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await scriptPermissionsService.getPermissionsByRole(scopeType, scopeId || null, 'executor');
      setPermissions(data.permissions || data || []);
    } catch (err) {
      onError(err.message);
      console.error('Error loading executor permissions:', err);
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, onError]);

  useEffect(() => {
    if (scopeType && (scopeId || scopeType === 'Global')) {
      loadPermissions();
    }
  }, [scopeType, scopeId, loadPermissions]);

  const handleRevoke = async (permissionId, scriptId, scriptName, principalName) => {
    onConfirm({
      show: true,
      message: `Are you sure you want to revoke executor access for ${principalName} on script "${scriptName}"?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          await scriptPermissionsService.removeScriptShare(scriptId, permissionId, scopeType, scopeId || null);
          await loadPermissions();
          onToast({ show: true, message: 'Executor access revoked successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to revoke access: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleBulkRevoke = () => {
    if (selectedPermissions.size === 0) return;
    onConfirm({
      show: true,
      message: `Are you sure you want to revoke executor access for ${selectedPermissions.size} permission(s)?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          // Get script IDs from selected permissions
          const permissionsToRevoke = permissions.filter(p => selectedPermissions.has(p.id));
          const scriptIds = [...new Set(permissionsToRevoke.map(p => p.scriptId))];
          
          await scriptPermissionsService.bulkRevokePermissions(scriptIds, 'executor', scopeType, scopeId || null);
          await loadPermissions();
          setSelectedPermissions(new Set());
          onToast({ show: true, message: 'Executor access revoked successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to revoke access: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const filteredPermissions = permissions.filter(p => {
    if (filters.search && !p.scriptName?.toLowerCase().includes(filters.search.toLowerCase()) && 
        !p.principalName?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.scriptId && p.scriptId?.toString() !== filters.scriptId) {
      return false;
    }
    return true;
  });

  return (
    <div>
      {/* Executor Capabilities Reference */}
      <div style={{ marginBottom: 20, padding: 16, background: '#fce7f3', borderRadius: 8, border: '1px solid #fbcfe8' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#9f1239' }}>Executor Capabilities</h4>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 14, color: '#831843' }}>
          <li>Can execute/run the script</li>
          <li>Can view script execution results</li>
          <li>Cannot view or modify script code</li>
          <li>Cannot edit script metadata or settings</li>
          <li>Cannot share the script with others</li>
        </ul>
      </div>

      {/* Filters and Actions */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by script or principal..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, flex: 1, minWidth: 200 }}
        />
        <input
          type="text"
          placeholder="Script ID"
          value={filters.scriptId}
          onChange={(e) => setFilters({ ...filters, scriptId: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, width: 120 }}
        />
        <button
          onClick={() => setShowAddDialog(true)}
          style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
        >
          Add Executor
        </button>
        {selectedPermissions.size > 0 && (
          <button
            onClick={handleBulkRevoke}
            style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
          >
            Revoke Selected ({selectedPermissions.size})
          </button>
        )}
      </div>

      {/* Permissions Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading executor permissions...</div>
      ) : filteredPermissions.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No executor permissions found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectedPermissions.size === filteredPermissions.length && filteredPermissions.length > 0}
                    onChange={() => {
                      if (selectedPermissions.size === filteredPermissions.length) {
                        setSelectedPermissions(new Set());
                      } else {
                        setSelectedPermissions(new Set(filteredPermissions.map(p => p.id)));
                      }
                    }}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Script</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Principal</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Granted</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Expires</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.map(perm => (
                <tr key={perm.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedPermissions.has(perm.id)}
                      onChange={() => {
                        const newSelected = new Set(selectedPermissions);
                        if (newSelected.has(perm.id)) {
                          newSelected.delete(perm.id);
                        } else {
                          newSelected.add(perm.id);
                        }
                        setSelectedPermissions(newSelected);
                      }}
                    />
                  </td>
                  <td style={{ padding: '12px' }}>{perm.scriptName || `Script #${perm.scriptId}`}</td>
                  <td style={{ padding: '12px' }}>{perm.principalName || perm.principalId}</td>
                  <td style={{ padding: '12px', textTransform: 'capitalize' }}>{perm.principalType || 'user'}</td>
                  <td style={{ padding: '12px', color: '#6b7280', fontSize: 12 }}>
                    {perm.grantedAt ? new Date(perm.grantedAt).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px', color: perm.expiresAt && new Date(perm.expiresAt) < new Date() ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                    {perm.expiresAt ? new Date(perm.expiresAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleRevoke(perm.id, perm.scriptId, perm.scriptName || `Script #${perm.scriptId}`, perm.principalName || perm.principalId)}
                      style={{ padding: '4px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Executor Dialog */}
      {showAddDialog && (
        <AddPermissionDialog
          role="executor"
          scopeType={scopeType}
          scopeId={scopeId}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            loadPermissions();
            onToast({ show: true, message: 'Executor access granted successfully', type: 'success' });
          }}
        />
      )}
    </div>
  );
};

// ==================== SHARE MANAGEMENT SECTION ====================
const ShareManagementSection = ({ scopeType, scopeId, onError, onToast, onConfirm }) => {
  const [allShares, setAllShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', roleFilter: '', scriptId: '' });
  const [selectedShares, setSelectedShares] = useState(new Set());
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadAllShares = useCallback(async () => {
    setLoading(true);
    try {
      // Load all scripts and their shares
      const scriptsData = await scriptPermissionsService.getScripts(scopeType, scopeId || null, { page: 1, pageSize: 1000 });
      const scripts = scriptsData.scripts || [];
      
      // Load shares for each script
      const sharesPromises = scripts.map(async (script) => {
        try {
          const permissions = await scriptPermissionsService.getScriptPermissions(script.id, scopeType, scopeId || null);
          return (permissions.shares || []).map(share => ({
            ...share,
            scriptId: script.id,
            scriptName: script.name
          }));
        } catch {
          return [];
        }
      });
      
      const sharesArrays = await Promise.all(sharesPromises);
      const allSharesFlat = sharesArrays.flat();
      setAllShares(allSharesFlat);
    } catch (err) {
      onError(err.message);
      console.error('Error loading shares:', err);
    } finally {
      setLoading(false);
    }
  }, [scopeType, scopeId, onError]);

  useEffect(() => {
    if (scopeType && (scopeId || scopeType === 'Global')) {
      loadAllShares();
    }
  }, [scopeType, scopeId, loadAllShares]);

  const handleRemoveShare = async (shareId, scriptId, scriptName, principalName) => {
    onConfirm({
      show: true,
      message: `Are you sure you want to remove share for ${principalName} on script "${scriptName}"?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          await scriptPermissionsService.removeScriptShare(scriptId, shareId, scopeType, scopeId || null);
          await loadAllShares();
          onToast({ show: true, message: 'Share removed successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to remove share: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleBulkRemove = () => {
    if (selectedShares.size === 0) return;
    onConfirm({
      show: true,
      message: `Are you sure you want to remove ${selectedShares.size} share(s)?`,
      onConfirm: async () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          // Remove each selected share
          const sharesToRemove = allShares.filter(s => selectedShares.has(s.id));
          await Promise.all(sharesToRemove.map(share => 
            scriptPermissionsService.removeScriptShare(share.scriptId, share.id, scopeType, scopeId || null)
          ));
          await loadAllShares();
          setSelectedShares(new Set());
          onToast({ show: true, message: 'Shares removed successfully', type: 'success' });
        } catch (err) {
          onError(err.message);
          onToast({ show: true, message: `Failed to remove shares: ${err.message}`, type: 'error' });
        }
      },
      onCancel: () => {
        onConfirm({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const filteredShares = allShares.filter(share => {
    if (filters.search && !share.scriptName?.toLowerCase().includes(filters.search.toLowerCase()) && 
        !share.principalName?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.roleFilter && share.role?.toLowerCase() !== filters.roleFilter.toLowerCase()) {
      return false;
    }
    if (filters.scriptId && share.scriptId?.toString() !== filters.scriptId) {
      return false;
    }
    return true;
  });

  const roleCounts = {
    viewer: filteredShares.filter(s => s.role === 'viewer').length,
    editor: filteredShares.filter(s => s.role === 'editor').length,
    executor: filteredShares.filter(s => s.role === 'executor').length
  };

  return (
    <div>
      {/* Filters and Actions */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by script or principal..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, flex: 1, minWidth: 200 }}
        />
        <select
          value={filters.roleFilter}
          onChange={(e) => setFilters({ ...filters, roleFilter: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        >
          <option value="">All Roles</option>
          <option value="viewer">Viewer ({roleCounts.viewer})</option>
          <option value="editor">Editor ({roleCounts.editor})</option>
          <option value="executor">Executor ({roleCounts.executor})</option>
        </select>
        <input
          type="text"
          placeholder="Script ID"
          value={filters.scriptId}
          onChange={(e) => setFilters({ ...filters, scriptId: e.target.value })}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, width: 120 }}
        />
        <button
          onClick={() => setShowAddDialog(true)}
          style={{ padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
        >
          Add Share
        </button>
        {selectedShares.size > 0 && (
          <button
            onClick={handleBulkRemove}
            style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
          >
            Remove Selected ({selectedShares.size})
          </button>
        )}
      </div>

      {/* Shares Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading shares...</div>
      ) : filteredShares.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No shares found</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectedShares.size === filteredShares.length && filteredShares.length > 0}
                    onChange={() => {
                      if (selectedShares.size === filteredShares.length) {
                        setSelectedShares(new Set());
                      } else {
                        setSelectedShares(new Set(filteredShares.map(s => s.id)));
                      }
                    }}
                  />
                </th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Script</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Principal</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Expires</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShares.map(share => {
                const isExpired = share.expiresAt && new Date(share.expiresAt) < new Date();
                return (
                  <tr key={share.id} style={{ borderBottom: '1px solid #e5e7eb', opacity: isExpired ? 0.6 : 1 }}>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="checkbox"
                        checked={selectedShares.has(share.id)}
                        onChange={() => {
                          const newSelected = new Set(selectedShares);
                          if (newSelected.has(share.id)) {
                            newSelected.delete(share.id);
                          } else {
                            newSelected.add(share.id);
                          }
                          setSelectedShares(newSelected);
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>{share.scriptName || `Script #${share.scriptId}`}</td>
                    <td style={{ padding: '12px' }}>{share.principalName || share.principalId}</td>
                    <td style={{ padding: '12px', textTransform: 'capitalize' }}>{share.principalType || 'user'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 4, fontSize: 12, textTransform: 'capitalize' }}>
                        {share.role}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: isExpired ? '#dc2626' : '#6b7280', fontSize: 12 }}>
                      {share.expiresAt ? new Date(share.expiresAt).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleRemoveShare(share.id, share.scriptId, share.scriptName || `Script #${share.scriptId}`, share.principalName || share.principalId)}
                        style={{ padding: '4px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Share Dialog */}
      {showAddDialog && (
        <BulkShareDialog
          scopeType={scopeType}
          scopeId={scopeId}
          onClose={() => setShowAddDialog(false)}
          onSuccess={() => {
            setShowAddDialog(false);
            loadAllShares();
            onToast({ show: true, message: 'Share added successfully', type: 'success' });
          }}
        />
      )}
    </div>
  );
};

// ==================== ADD PERMISSION DIALOG (for Viewer/Editor/Executor sections) ====================
const AddPermissionDialog = ({ role, scopeType, scopeId, onClose, onSuccess }) => {
  const [scriptId, setScriptId] = useState('');
  const [availableScripts, setAvailableScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [principalType, setPrincipalType] = useState('user');
  const [principalId, setPrincipalId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadScripts = async () => {
      setLoadingScripts(true);
      try {
        const data = await scriptPermissionsService.getScripts(scopeType, scopeId || null, { page: 1, pageSize: 1000 });
        setAvailableScripts(data.scripts || []);
      } catch (err) {
        console.error('Error loading scripts:', err);
      } finally {
        setLoadingScripts(false);
      }
    };
    loadScripts();
  }, [scopeType, scopeId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!scriptId || !principalId.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await scriptPermissionsService.bulkShareScripts(
        [parseInt(scriptId)],
        principalType,
        principalId.trim(),
        role,
        expiresAt ? new Date(expiresAt).toISOString() : null,
        scopeType,
        scopeId || null
      );
      onSuccess();
    } catch (err) {
      console.error('Error adding permission:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 500, width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Add {role.charAt(0).toUpperCase() + role.slice(1)} Permission</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280', padding: '0 8px', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Script</label>
              <select
                value={scriptId}
                onChange={(e) => setScriptId(e.target.value)}
                required
                disabled={loadingScripts}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}
              >
                <option value="">Select a script...</option>
                {availableScripts.map(script => (
                  <option key={script.id} value={script.id}>{script.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Principal Type</label>
              <select
                value={principalType}
                onChange={(e) => {
                  setPrincipalType(e.target.value);
                  setPrincipalId('');
                }}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}
              >
                <option value="user">User</option>
                <option value="team">Team</option>
                <option value="org">Organization</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Principal ID</label>
              <input
                type="text"
                value={principalId}
                onChange={(e) => setPrincipalId(e.target.value)}
                placeholder={`Enter ${principalType} ID or email`}
                required
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Expires At (optional)</label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{ padding: '10px 20px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: submitting ? 'not-allowed' : 'pointer', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !scriptId || !principalId.trim()}
              style={{ padding: '10px 20px', background: submitting || !scriptId || !principalId.trim() ? '#9ca3af' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: submitting || !scriptId || !principalId.trim() ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}
            >
              {submitting ? 'Adding...' : 'Add Permission'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== BULK SHARE DIALOG (for Share Management section) ====================
const BulkShareDialog = ({ scopeType, scopeId, onClose, onSuccess }) => {
  const [selectedScriptIds, setSelectedScriptIds] = useState(new Set());
  const [availableScripts, setAvailableScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [principalType, setPrincipalType] = useState('user');
  const [principalId, setPrincipalId] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadScripts = async () => {
      setLoadingScripts(true);
      try {
        const data = await scriptPermissionsService.getScripts(scopeType, scopeId || null, { page: 1, pageSize: 1000 });
        setAvailableScripts(data.scripts || []);
      } catch (err) {
        console.error('Error loading scripts:', err);
      } finally {
        setLoadingScripts(false);
      }
    };
    loadScripts();
  }, [scopeType, scopeId]);

  const toggleScriptSelection = (scriptId) => {
    const newSelected = new Set(selectedScriptIds);
    if (newSelected.has(scriptId)) {
      newSelected.delete(scriptId);
    } else {
      newSelected.add(scriptId);
    }
    setSelectedScriptIds(newSelected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!principalId.trim() || selectedScriptIds.size === 0) {
      return;
    }

    setSubmitting(true);
    try {
      await scriptPermissionsService.bulkShareScripts(
        Array.from(selectedScriptIds),
        principalType,
        principalId.trim(),
        selectedRole,
        expiresAt ? new Date(expiresAt).toISOString() : null,
        scopeType,
        scopeId || null
      );
      onSuccess();
    } catch (err) {
      console.error('Error sharing scripts:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 600, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Share Scripts</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: '#6b7280', padding: '0 8px', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Select Scripts</label>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8 }}>
                {loadingScripts ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>Loading scripts...</div>
                ) : availableScripts.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No scripts available</div>
                ) : (
                  availableScripts.map(script => (
                    <label key={script.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedScriptIds.has(script.id)}
                        onChange={() => toggleScriptSelection(script.id)}
                      />
                      <span style={{ fontSize: 14 }}>{script.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedScriptIds.size > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                  {selectedScriptIds.size} script(s) selected
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Principal Type</label>
              <select
                value={principalType}
                onChange={(e) => {
                  setPrincipalType(e.target.value);
                  setPrincipalId('');
                }}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}
              >
                <option value="user">User</option>
                <option value="team">Team</option>
                <option value="org">Organization</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Principal ID</label>
              <input
                type="text"
                value={principalId}
                onChange={(e) => setPrincipalId(e.target.value)}
                placeholder={`Enter ${principalType} ID or email`}
                required
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14 }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="executor">Executor</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Expires At (optional)</label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{ padding: '10px 20px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: submitting ? 'not-allowed' : 'pointer', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !principalId.trim() || selectedScriptIds.size === 0}
              style={{ padding: '10px 20px', background: submitting || !principalId.trim() || selectedScriptIds.size === 0 ? '#9ca3af' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: submitting || !principalId.trim() || selectedScriptIds.size === 0 ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 500 }}
            >
              {submitting ? 'Sharing...' : `Share ${selectedScriptIds.size} Script(s)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScriptGovernanceTab;

